import React, { useState, useMemo, useCallback } from 'react';
import moment from 'moment';

import CancelIcon from '../../assets/icons/CancelIcon';
import RequestIcon from '../../assets/icons/RequestIcon';
import OverdueIcon from '../../assets/icons/OverdueIcon';
import CompletedIcon from '../../assets/icons/CompletedIcon';
import ProcedureIcon from '../../assets/icons/ProcedureIcon';
import ProductIcon from '../../assets/icons/ProductIcon';
import KitsIcon from '../../assets/icons/KitIcon';
import FolderIcon from '../../assets/icons/FolderIcon';
import ShippingIcon from '../../assets/icons/ShippingIcon';
import PreferenceIcon from '../../assets/icons/PreferenceIcon';
import BookedIcon from '../../assets/icons/BookedIcon';
// import TransferIcon from '../../assets/icons/TransferIcon';

import CaseGuidanceModal from './modals/CaseGuidanceModal';
import CasePreferencesModal from './modals/CasePreferencesModal';

import { getCommaSeparatedNames } from '../../utils/table';

import { statusColors } from '../../constants/cases';
import { kitVariantTypes, caseStatusOptions } from '../../constants/enums';
import CheckIcon from '../../assets/icons/CheckIcon';
import ReceiptIcon from '../../assets/icons/ReceiptIcon';

const getStatusIcon = (status, filled, canceled, proformaSent, orderClosed, color) => {
  if (canceled) {
    return <CancelIcon color={color} />;
  }

  switch (status) {
    case caseStatusOptions.request:
      return <RequestIcon color={color} />;
    case caseStatusOptions.booked:
      return <BookedIcon color={color} />;
    case caseStatusOptions.overdue:
      return <OverdueIcon color={color} />;
    case caseStatusOptions.completed:
      if (proformaSent) {
        if (orderClosed) {
          return <CheckIcon color={color} />
        } else {
          return <ReceiptIcon color={color} />
        }
      }
      return <CompletedIcon color={color} />;
    default:
      return <RequestIcon color={color} />;
  }
};

const CaseInfo = (props) => {
  const {
    activeCase,
    hospitals,
    procedures,
    procedureOptions,
    surgeons,
    products: productsList,
    kits: kitsList,
    fetching,
    caseGuidance,
    casePreferences,
    onCaseGuidanceModalOpen,
    onCasePreferencesModalOpen,
    view,
    minimized = false,
    tenantColor,
    onAddKitsClick,
    users = [],
    additional,
    procedureEquipmentLists = [],
  } = props;
  const {
    active = true,
    date,
    time = '',
    status,
    hospital,
    procedure,
    procedureOption,
    surgeon,
    products,
    kits,
    patientReference,
    kitVariant,
    deliveryAddress,
    noEquipmentToShip,
    createdAt,
    createdBy,
    proformaSent,
    orderClosed,
    procedureEquipmentList,
  } = activeCase;
  const {
    label = '',
    street = '',
    city = '',
    state = '',
  } = deliveryAddress || {};

  const [caseGuidanceModalOpen, toggleCaseGuidanceModal] = useState(false);
  const [casePreferencesModalOpen, toggleCasePreferencesModal] = useState(false);

  const hospitalName = useMemo(() => {
    const caseHospital = hospitals.find((item) => item.id === hospital);
    return caseHospital ? `${caseHospital.name}, ${caseHospital.state}` : '';
  }, [hospital, hospitals]);
  const procedureName = useMemo(() => {
    const caseProcedure = procedures.find((item) => item.id === procedure);
    return caseProcedure ? caseProcedure.name : '';
  }, [procedure, procedures]);
  const procedureOptionName = useMemo(() => {
    const option = procedureOptions.find((item) => item.id === procedureOption);
    return option ? option.name : '';
  }, [procedureOption, procedureOptions]);
  const surgeonName = useMemo(() => {
    const caseSurgeon = surgeons.find((item) => item.id === surgeon);
    return caseSurgeon ? `${caseSurgeon.title} ${caseSurgeon.firstName} ${caseSurgeon.lastName}` : '';
  }, [activeCase, surgeons]);
  const productNames = useMemo(() => getCommaSeparatedNames(productsList, products), [products, productsList]);
  const kitNames = useMemo(() => getCommaSeparatedNames(kitsList, kits), [kits, kitsList]);

  const getUserName = useCallback((uid) => {
    const user = users.find((u) => u.uid === uid);
    return user ? `${user.firstName} ${user.lastName}` : '';
  }, [users]);

  const procedureEquipmentName = useMemo(() => {
    const equipmentList = procedureEquipmentLists.find((item) => item.id === procedureEquipmentList);
    return equipmentList ? equipmentList.name : '';
  },[procedureEquipmentList, procedureEquipmentLists])

  const iconColor = 'var(--app-text)';
  const statusIconColor = active ? statusColors[status] : statusColors.OVERDUE;

  return (
    <div className='case-info__container'>
      <div className='case-info'>
        <div className={`case-info__main ${status}`}>
          <div>
            <div className='d-flex'>
              {getStatusIcon(status, false, !active, proformaSent, orderClosed, statusIconColor)}
              <div className='font-size-bg font-bold m-l-md m-r-sm'>
                {date ? moment(date).local().format("dddd D MMMM YYYY") : ''}
              </div>
              <div className='font-bold' style={{ textTransform: 'lowercase' }}>{time}</div>
              {/*<div className='m-l-md'>*/}
              {/*  <Tooltip*/}
              {/*    title='Add to Google Calendar'*/}
              {/*    aria-label='calendar'*/}
              {/*  >*/}
              {/*    <IconButton*/}
              {/*      aria-controls='calendar'*/}
              {/*      aria-haspopup='true'*/}
              {/*      color='primary'*/}
              {/*      onClick={onAddToCalendar}*/}
              {/*      size='small'*/}
              {/*    >*/}
              {/*      <CalendarIcon color={tenantColor} />*/}
              {/*    </IconButton>*/}
              {/*  </Tooltip>*/}
              {/*</div>*/}
            </div>
            <div className='m-t-md'>
              <div className='font-size-bg font-bold'>{surgeonName}</div>
              <div>{hospitalName}</div>
              <div>{patientReference}</div>
            </div>
            {!!kitVariant && status !== caseStatusOptions.request && (
              <div className='m-t-lg d-flex'>
                <div
                  className='case-kit-variant'
                  style={{
                    color: kitVariant === kitVariantTypes.consignment ? statusColors.REQUEST : statusColors.BOOKED,
                    borderColor: kitVariant === kitVariantTypes.consignment ? statusColors.REQUEST : statusColors.BOOKED,
                    background: 'var(--app-surface)'
                  }}
                >
                  {kitVariant === kitVariantTypes.consignment ? 'Consignment' : 'Loan'}
                </div>
                {additional && (
                  <div
                    className='m-l-md case-kit-variant'
                    style={{
                      color: statusColors.BOOKED,
                      borderColor: statusColors.BOOKED,
                      background: 'var(--app-surface)'
                    }}
                  >
                    Loan
                  </div>
                )}
              </div>
            )}
          </div>
          {/*<div>*/}
          {/*  <div className='case-status' style={{ background: active ? statusColors[status] : statusColors.OVERDUE }}>*/}
          {/*    <div className='d-flex direction-column space-between'>*/}
          {/*      {getStatusIcon(status, false, !active)}*/}
          {/*      <div className='case-status-label'>*/}
          {/*        {getStatusLabel(status, !active)}*/}
          {/*      </div>*/}
          {/*    </div>*/}
          {/*  </div>*/}
          {/*</div>*/}
        </div>

        {!minimized && (
          <>
            <div className='case-info__block'>
              <div className='d-flex align-start m-t-sm'>
                <ProcedureIcon color={iconColor} />
                <span className='m-l-lg'>{`${procedureName}${procedureOptionName ? `, ${procedureOptionName}` : ''}`}</span>
              </div>
              <div className='d-flex align-start m-t-md'>
                <ProductIcon color={iconColor} />
                <span className='m-l-lg'>{procedureEquipmentList ? procedureEquipmentName : productNames}</span>
              </div>

              <div className='d-flex align-start m-t-md'>
                <KitsIcon color={iconColor} />
                {noEquipmentToShip ? (
                  <span className='m-l-lg'>No equipment to ship</span>
                ) : (
                  kits && kits.length ? (
                    <span className='m-l-lg'>{kitNames}</span>
                  ) : (
                    <span
                      className='m-l-lg pointer font-bold'
                      style={{ color: tenantColor }}
                      onClick={onAddKitsClick}
                    >
                        Add Kits
                      </span>
                  )
                )}
              </div>

              {deliveryAddress && (
                <div className='d-flex align-start m-t-md'>
                  <ShippingIcon color={iconColor} />
                  <div className='m-l-lg'>
                    <span className='font-bold'>{label}</span>
                    <span className='m-l-sm'>({[street, city, state].filter((v) => !!v).join(', ')})</span>
                  </div>
                </div>
              )}

              {status !== caseStatusOptions.request && !!createdAt && !!createdBy && (
                <div className='d-flex align-start m-t-md'>
                  <BookedIcon color={iconColor} />
                  <div className='m-l-lg'>
                    <span className='font-bold'>{getUserName(createdBy)}</span>
                    <span className='m-l-sm'>booked on {moment(createdAt).format('DD/MM/YYYY')}</span>
                  </div>
                </div>
              )}
            </div>

            {/*<div className='divider' />*/}

            <div className='d-flex direction-column space space-between align-start flex-1'>
              <div className='case-buttons'>
                <div
                  className='case-button'
                  onClick={() => {
                    onCaseGuidanceModalOpen();
                    toggleCaseGuidanceModal(true);
                  }}
                >
                  <FolderIcon color={iconColor} />
                  <div className='font-bold m-t-md' style={{ fontSize: 13 }}>Case Guidance</div>
                  <div className='font-size-sm secondary'>Click to view</div>
                </div>
                <div
                  className='case-button'
                  onClick={() => {
                    onCasePreferencesModalOpen();
                    toggleCasePreferencesModal(true);
                  }}
                >
                  <PreferenceIcon color={iconColor} />
                  <div className='font-bold m-t-md' style={{ fontSize: 13 }}>Surgeon Preference</div>
                  <div className='font-size-sm secondary'>Click to view</div>
                </div>

                <div />

                {/*<div*/}
                {/*  className='case-button'*/}
                {/*  onClick={() => {*/}
                {/*    onCasePreferencesModalOpen();*/}
                {/*    toggleCasePreferencesModal(true);*/}
                {/*  }}*/}
                {/*>*/}
                {/*  <TransferIcon />*/}
                {/*  <div className='font-bold m-t-md' style={{ fontSize: 13 }}>Transfer Case</div>*/}
                {/*  <div className='font-size-sm secondary'>Manage Case Transfer</div>*/}
                {/*</div>*/}
              </div>
            </div>
          </>
        )}

        {minimized && (view === 'SETS' || view === 'USAGE' || view === 'PROFORMA') && (
          <div className='case-info__block'>
            <div className='d-flex align-start m-t-sm'>
              <ProcedureIcon color={iconColor} />
              <span className='m-l-lg'>{`${procedureName}${procedureOptionName ? `, ${procedureOptionName}` : ''}`}</span>
            </div>

            {deliveryAddress && (
              <div className='d-flex align-start m-t-md'>
                <ShippingIcon color={iconColor} />
                <div className='m-l-lg'>
                  <span className='font-bold'>{label}</span>
                  <span className='m-l-sm'>({[street, city, state].filter((v) => !!v).join(', ')})</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <CaseGuidanceModal
        open={caseGuidanceModalOpen}
        onClose={() => toggleCaseGuidanceModal(false)}
        files={caseGuidance}
        fetching={fetching}
      />
      <CasePreferencesModal
        open={casePreferencesModalOpen}
        onClose={() => toggleCasePreferencesModal(false)}
        preferences={casePreferences}
        fetching={fetching}
      />
    </div>
  );
};

export default CaseInfo;
